// ignore-string-externalization

import React from 'react';
import TrackVisibility from 'react-on-screen';
import { ThemeProvider } from 'styled-components';
import { TEMPLATE_THEMES, MODULE_THEMES } from '../Theme';
import { Stack, Box } from '../ui';
import { Module } from '../Module';
import { TemplateType } from '.';
import { useDynamicNavBarColor } from './useDynamicNavBarColor';
import { ModuleType, ModuleCarouselType } from '../Module/types';

export type LandingPageTemplateType = TemplateType & {
  heroModule?: ModuleType;
  modules?: ModuleType[];
};

const carouselSlideIfPresent = (mod?: ModuleType) =>
  mod && 'carouselSlides' in mod
    ? (mod as ModuleCarouselType).carouselSlides?.[0]
    : mod;

export const LandingPageTemplate: React.FC<
  LandingPageTemplateType
> = template => {
  const { theme = 'Dark', heroModule, modules = [] } = template;
  const [activeSlide, setActiveSlide] = React.useState<
    ModuleType | undefined
  >();

  const tplTheme = TEMPLATE_THEMES[theme];

  const modTheme = (mod: ModuleType) => ({
    ...tplTheme,
    ...(MODULE_THEMES[mod.themeName || ''] || {}),
  });

  const isFullBleed = (mod: ModuleType) =>
    [
      'relatedLinksModule',
      'editorialGridModule',
      'heroBodyModule',
      'headerModule',
      'heroBodyCarousel',
      'heroHeaderCarousel',
      'heroNarrativeCarousel',
      'eventList',
      'recirculationModule',
      'videoModule',
      'miniCarouselNarrative',
    ].includes(mod.contentType);

  const allModules: any = [heroModule, ...(modules || [])].filter(m =>
    Boolean(m?.contentType),
  );

  useDynamicNavBarColor(activeSlide || carouselSlideIfPresent(allModules?.[0]));

  const renderModule = React.useCallback((mod: ModuleType) => {
    const isHeader = mod.id === heroModule?.id;
    const isCarouselModule = 'carouselSlides' in mod;
    const modProps = {
      isHeader,
      // for carousel headers, track slide change so we can grab current color for nav bar
      onShowSlide: isHeader && isCarouselModule ? setActiveSlide : undefined,
      ...mod,
    } as any;

    // don't use the 'once' property here on TrackVisibility- we need our
    // own implementation of it, where we update the visibility but don't
    // fire the tracking event
    return (
      <TrackVisibility offset={'carouselSlides' in mod ? 200 : 50}>
        {({ isVisible }) => (
          <div>
            <Module {...modProps} shouldAnimate isVisible={isVisible} />
          </div>
        )}
      </TrackVisibility>
    );
  }, []);

  return (
    <Stack data-testid={`landing-${template.urlSlug || 'unknown'}`}>
      {allModules.map((mod: ModuleType, idx: number) => (
        <ThemeProvider key={`${mod.id}-${idx}`} theme={modTheme(mod)}>
          <Box noPadding={isFullBleed(mod)} transparent={idx === 0}>
            {renderModule(mod)}
          </Box>
        </ThemeProvider>
      ))}
    </Stack>
  );
};
